import {
  inject,
  Input,
  Output,
  Component,
  DestroyRef,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { Principal } from 'src/app/core/models/object.models';
import { FormMode } from 'src/app/core/models/form.models';

import { NomObjectClass, PopupType } from 'src/app/core/constants';

import { AppState } from 'src/app/core/state/app.state';
import {
  selectPricipalsSelectedMap,
  selectPrincipalsCurrentCompanyId,
  selectUser,
} from 'src/app/core/state';

import {
  BehaviorSubject,
  filter,
  finalize,
  map,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  setOriginalPrincipals,
  removeTemporaryPrincipal,
  setCurrentCompany,
  initializeCompanyWithPrincipals,
  snapshotTemporaryChanges,
  restoreTemporaryChanges,
  commitTemporaryChanges,
} from 'src/app/core/state/principals.actions';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import { PickerDataobjectsDialogComponent } from 'src/app/objects/dialogs/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { SuiButtonComponent } from '@sui/button';
import { NgClass, AsyncPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PrincipalCardComponent } from '../../../shared/components/cards/principal-card/principal-card.component';
import { GeneralFilterPipe } from '../../../shared/pipes/general-filter.pipe';

@Component({
  selector: 'app-user-group-members',
  templateUrl: './user-group-members.component.html',
  styleUrl: './user-group-members.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiButtonComponent,
    NgClass,
    FaIconComponent,
    PrincipalCardComponent,
    AsyncPipe,
    TranslatePipe,
    GeneralFilterPipe,
  ],
})
export class UserGroupMembersComponent {
  // ! companyId is null or a string in certain contexts.
  // ! In other contexts is not provided - undefined.
  // ! Maybe there we need to pass again the companyId - we shall see.
  @Input() companyId: string | null | undefined = undefined;
  @Input() principals: Principal[] = [];
  @Input() searchString$: BehaviorSubject<string>;
  @Input() formMode: FormMode;

  @Output() principalsChanged = new EventEmitter<Array<Principal>>();

  temporalSelectedMembersMap$: Observable<Map<string, Principal>>;
  temporalSelectedMembersList$: Observable<Array<Principal>>;

  membersPerPage: Array<Principal>;
  membersLoading: boolean = false;
  membersTotal: number;
  temporalSelectedMembersMap: Map<string, Principal> = new Map<
    string,
    Principal
  >();
  temporalSelectedMembersList: Principal[];

  searchString: string = '';
  companyPrincipals: Principal[] = [];
  filter: 'all' | 'users' | 'user-groups' = 'all';
  currentMatchCount: number = 0;
  filterTabs: any[] = [
    {
      text: this.translateService.instant('user-group-members.label.all'),
      key: 'all',
      icon: ['fas', 'people-group'],
    },
    {
      text: this.translateService.instant('user-group-members.label.users'),
      key: 'users',
      icon: ['fas', 'user'],
    },
    {
      text: this.translateService.instant(
        'user-group-members.label.user-groups',
      ),
      key: 'user-groups',
      icon: ['fas', 'users-gear'],
    },
  ];
  popupReference: any;

  private destroyRef = inject(DestroyRef);
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private cdr = inject(ChangeDetectorRef);

  NomObjectClass = NomObjectClass;

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private dialogService: DialogService,
  ) {
    // * membersPerPage$ are the Company members (Users in the Company)
    // * @Input() principals are already selected Members - comes from the BE
    this.temporalSelectedMembersMap$ = this.store.select(
      selectPricipalsSelectedMap,
    );
    this.temporalSelectedMembersList$ = this.temporalSelectedMembersMap$.pipe(
      map((principalMap) => Array.from(principalMap.values())),
    );
  }

  ngOnInit(): void {
    if (this.searchString$) {
      this.searchString$
        .pipe(
          tap((val) => {
            this.searchString = val;
            this.updateMatchCount();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    if (this.principals == undefined) {
      this.principals = [];
    }

    // * Use actual FormMode from parent for reliable detection
    // * Edit mode: has existing principals that must be preserved
    // * Create mode: starts with empty principals, company changes should clear selections
    const isEditMode = this.formMode === FormMode.Edit;

    // ! below 2 cases are for User Groups flow!
    if (isEditMode) {
      // * EDIT MODE: Set original principals FIRST to avoid cleanup interference
      // * This ensures the existing members are preserved and displayed
      this.store.dispatch(
        initializeCompanyWithPrincipals({
          companyId: this.companyId,
          principals: this.principals,
        }),
      );
    } else {
      // * CREATE MODE: Set company context FIRST for proper cleanup behavior
      // * This ensures company changes will clear any selected principals
      this.store.dispatch(setCurrentCompany({ companyId: this.companyId }));
      this.store.dispatch(
        setOriginalPrincipals({ principals: this.principals }),
      );

      // WAP-1438: the dynamic form applies inputs once, so the target company picked in the General
      // Settings tab arrives through the store - enable "Add members" and load that company's principals
      this.store
        .select(selectPrincipalsCurrentCompanyId)
        .pipe(
          filter(
            (companyId) => Boolean(companyId) && companyId !== this.companyId,
          ),
          tap((companyId) => {
            this.companyId = companyId;
            this.membersLoading = true;
            this.fetchPrincipals(
              companyId,
              1,
              this.searchString$?.value ?? '',
              [NomObjectClass.USER, NomObjectClass.USER_GROUP],
            );
            this.cdr.markForCheck();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    if (this.companyId) {
      this.membersLoading = true;
      this.fetchPrincipals(this.companyId, 1, this.searchString$?.value ?? '', [
        NomObjectClass.USER,
        NomObjectClass.USER_GROUP,
      ]);
    } else {
      this.store
        .select(selectUser)
        .pipe(
          take(1),
          tap((user) => {
            this.membersLoading = true;
          }),
          switchMap((user) =>
            this.utilsDataObjectApi.fetchCompanyPrincipals(
              user.active_company_id,
              1,
              this.searchString$?.value ?? '',
              [NomObjectClass.USER, NomObjectClass.USER_GROUP],
            ),
          ),
          tap((response: any) => {
            this.membersPerPage = response.items;
            this.membersTotal = response.total;
          }),
          finalize(() => {
            this.membersLoading = false;
            this.cdr.markForCheck();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    // ! Listen to computed selectedPrincipals and emit changes
    this.temporalSelectedMembersList$
      .pipe(
        tap((computedPrincipals) => {
          // * Emit the computed result (original + temporary changes) to parent
          this.principalsChanged.emit(computedPrincipals);
          this.updateMatchCount();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Updates the current match count based on search string
   */
  private updateMatchCount(): void {
    if (!this.searchString || !this.searchString.trim()) {
      this.currentMatchCount = 0;
      return;
    }

    const search = this.searchString.toLowerCase().trim();

    // Get current principals synchronously from the observable's last value
    this.temporalSelectedMembersList$.pipe(take(1)).subscribe((principals) => {
      this.currentMatchCount = (principals || []).filter((p) =>
        p.name?.toLowerCase().includes(search),
      ).length;
    });
  }

  deletePrincipal(principal: Principal) {
    // ! use temporary removal action instead of direct array manipulation
    this.store.dispatch(
      removeTemporaryPrincipal({ principalId: principal.id }),
    );
  }

  addMembers() {
    // * Snapshot current temporary changes before opening child popup
    this.store.dispatch(snapshotTemporaryChanges());

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds: [NomObjectClass.USER],
          companyId: this.companyId,
          popupType: PopupType.SELECT_USERS,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.subscribe((event: any) => {
      if (
        event.reason === DialogCloseReason.Save ||
        event.reason === DialogCloseReason.SaveAndClose
      ) {
        this.store.dispatch(commitTemporaryChanges());
      } else if (
        event.reason === DialogCloseReason.Cancel ||
        event.reason === DialogCloseReason.DiscardChanges ||
        event.reason === DialogCloseReason.Escape
      ) {
        this.store.dispatch(restoreTemporaryChanges());
      }

      console.log(
        '%c Dialog closed with event:',
        'color: green; font-weight: bold;',
        event,
      );
    });
  }

  /**
   * Returns the count of principals matching the current search string
   * Used by parent form to determine if this tab has search matches
   */
  getSearchStringMatchesCount(): number {
    return this.currentMatchCount;
  }

  fitlerItems(filterKey: 'all' | 'users' | 'user-groups') {
    this.filter = filterKey;
  }

  showPrincipal(principal): boolean {
    if (this.filter === 'all') return true;
    if (this.filter === 'users')
      return principal.object_class_id === NomObjectClass.USER;
    if (this.filter === 'user-groups')
      return principal.object_class_id === NomObjectClass.USER_GROUP;
    return true;
  }

  get showFilters(): boolean {
    return (
      this.principals.some(
        (principal) => principal.object_class_id === NomObjectClass.USER,
      ) &&
      this.principals.some(
        (principal) => principal.object_class_id === NomObjectClass.USER_GROUP,
      )
    );
  }

  fetchPrincipals(
    companyId: string,
    currentPage: number,
    query: string,
    objectClassIds: NomObjectClass[],
  ) {
    this.utilsDataObjectApi
      .fetchCompanyPrincipals(companyId, currentPage, query, objectClassIds)
      .pipe(
        tap((response) => {
          this.membersPerPage = response.items;
          this.membersTotal = response.total;
        }),
        finalize(() => {
          this.membersLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
